import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { FieldWrapper, FormlyFieldConfig } from '@ngx-formly/core';
import { TextFieldComponent } from '../component/text-field/text-field.component';
import { FormDataService } from '../service/form-data.service';
import { LoadingService } from '../service/loading.service';
import {
  AddressInformation,
  AddressValues,
  Form,
  FormElements,
  FormSections,
  FormTemplateOptions,
  EmailPhoneValues,
  PrefixSuffix,
  Selection,
  SubmitCreditCardAndEFTPaymentRequest,
  SubmitCreditCardPaymentRequest,
  SubmitEFTPaymentRequest,
  SubmitInvoicePaymentRequest,
  SubmitOfflinePaymentRequest,
  SubmitPaymentResponse
} from '../interfaces/form-model';
import { TextAreaComponent } from '../component/text-area/text-area.component';
import { NumberInputComponent } from '../component/number-input/number-input.component';
import { DatePickerComponent } from '../component/date-picker/date-picker.component';
import { CheckboxComponent } from '../component/checkbox/checkbox.component';
import { RadioComponent } from '../component/radio/radio.component';
import { SingleSelectComponent } from '../component/single-select/single-select.component';
import { MultiSelectComponent } from '../component/multi-select/multi-select.component';
import { FieldWrapperComponent } from "../component/wrappers/field-wrapper/field-wrapper.component";
import { LabelComponent } from "../component/label/label.component";
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  Observable,
  of,
  skip,
  Subscription,
  switchMap,
  tap,
  zip
} from "rxjs";
import { FormSaveService } from "../service/form-save.service";
import { FormSubmitService } from "../service/form-submit.service";
import { FormFinishService } from "../service/form-finish.service";
import { AddressComponent } from '../component/composite/address/address.component';
import { NameFieldComponent } from "../component/composite/name-field/name-field.component";
import { LoginExistingComponent } from '../component/composite/login-existing/login-existing.component';
import { GeneralSectionComponent } from '../component/sections/general-section/general-section.component';
import { LoginSectionComponent } from '../component/sections/login-section/login-section.component';
import { FileFieldComponent, UploadType } from "../component/file-field/file-field.component";
import { LoginNewComponent } from '../component/composite/login-new/login-new.component';
import { FormlyFieldProps } from "@ngx-formly/core/lib/models/fieldconfig";
import { EventSectionComponent } from '../component/sections/event-section/event-section.component';
import { PaymentSectionComponent } from "../component/sections/payment-section/payment-section.component";
import { PaymentStateService } from "../service/payment-state.service";
import { CalendarService } from "../service/calendar.service";
import {
  GenericHeaderWrapperComponent
} from "../component/wrappers/generic-header-wrapper/generic-header-wrapper.component";
import { RegistrantWrapperComponent } from '../component/wrappers/registrant-wrapper/registrant-wrapper.component';
import { EventButtonComponent } from '../component/event-button/event-button.component';
import { MembershipSectionComponent } from "../component/sections/membership-section/membership-section.component";
import { CaptchaComponent } from "../component/captcha/captcha.component";
import {
  CreateLinkedProfileButtonComponent
} from "../component/create-linked-profile-button/create-linked-profile-button.component";
import { CvvComponent } from "../component/cvv/cvv.component";
import { EftCheckExampleComponent } from "../component/eft-check-example/eft-check-example.component";
import { EftAuthorizeAgreementComponent } from "../component/eft-authorize-agreement/eft-authorize-agreement.component";
import { HttpErrorResponse } from "@angular/common/http";
import { SummarySectionComponent } from '../component/sections/summary-section/summary-section.component';
import { ReviewPanelSectionComponent } from "../component/sections/review-panel-section/review-panel-section.component";
import { GenericModalService } from "../service/generic-modal.service";
import { CaptchaService } from "../service/captcha.service";
import { SectionErrorMessageComponent } from "../component/section-error-message/section-error-message.component";
import { EmailPhoneComponent } from '../component/composite/email-phone/email-phone.component';
import { SlAlert } from '@shoelace-style/shoelace';
import { FormEventRegistrationService } from '../service/form-event-registration.service';
import { TimerService } from '../service/timer.service';
import { WrapperType } from "../interfaces/form-model";
import { WhiteSpaceComponent } from '../component/white-space/white-space.component';
import { LineSeparatorComponent } from '../component/line-separator/line-separator.component';

type AdditionalProperties = {
  [additionalProperties: string]: any;
};
type TransformElementsIntoFieldConfig = (element: FormElements) => FormlyFieldConfig;

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class FormComponent implements OnInit, OnDestroy {

  formData!: Form;
  form = new FormGroup({});
  model = {};
  fields: FormlyFieldConfig[] = [];
  fieldsChangeSubscription: Subscription = new Subscription();
  pagerButtonsEnableSubscription: Subscription = new Subscription();
  pagerButtonsDisableSubscription: Subscription = new Subscription();
  initialFormValue$ = new BehaviorSubject({});
  disablePager: boolean = false;
  submissionId!: number;
  hasLayouts = false;
  error: boolean = false;
  errorMessage: string = '';
  refreshReservationsSubscriptions: Map<string | undefined, Subscription> = new Map();
  isTimerVisible = false;

  private readonly registrantBodyElementClass = 'registrant-body__elements';

  constructor(
    private calendarService: CalendarService,
    private formDataService: FormDataService,
    private formSaveService: FormSaveService,
    private formSubmitService: FormSubmitService,
    private formFinishService: FormFinishService,
    private paymentStateService: PaymentStateService,
    private loadingService: LoadingService,
    private genericModalService: GenericModalService,
    private captchaService: CaptchaService,
    private formEventRegistrationService: FormEventRegistrationService,
    private timerService: TimerService,
    @Inject('CURRENT_FORM_ID') private currentFormId: string,
    @Inject('TAKE_FORM') private takeForm: string,
    @Inject('DISABLE_EMAIL_CONFIRMATION') private disableEmailConfirmation: string,
    @Inject('TIMEZONE_SETTING') private timezoneSetting: string
  ) { }

  ngOnInit(): void {

    const id = this.getFormId();

    this.timerService.showTimer$.subscribe((showTimer) => {
      this.isTimerVisible = showTimer;
    });

    if (id) {
      this.showLoading();
      this.formDataService.getForm(id, this.getTakeForm(), this.getDisableEemailConfirmation())
        .subscribe((data: Form) => this.handleFormRetrievalSuccess(data));
    }
  }

  ngOnDestroy() {
    this.fieldsChangeSubscription?.unsubscribe();
    this.initialFormValue$.unsubscribe();
    this.pagerButtonsEnableSubscription?.unsubscribe();
    this.pagerButtonsDisableSubscription?.unsubscribe();
    this.timerService.showTimer$.unsubscribe();
    this.refreshReservationsSubscriptions.forEach(sub => sub.unsubscribe());
    this.refreshReservationsSubscriptions.clear();
  }

  getTakeForm(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("takeForm")) {
      return urlParams.get('takeForm') === '1';
    }

    // @ts-ignore
    return '1' === this.takeForm;
  }

  getDisableEemailConfirmation(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("dsbl")) {
      return ["true", "1"].includes(String(urlParams.get('dsbl')));
    }

    // @ts-ignore
    return '1' === this.disableEmailConfirmation;
  }

  getFormId(): string {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("id")) {
      return urlParams.get('id') as string;
    }

    // @ts-ignore
    return this.currentFormId || "";
  }

  initFormValueChangeSubscription(): Subscription {

    // We want to capture the first reactive form value, this first reactive value is initialized by Formly and is out of our control.
    // We capture it by grabbing the first change event and applying it to a BehaviorSubject to allow the value to be updated and played back.
    this.form.valueChanges.pipe(first()).subscribe(value => this.initialFormValue$.next(value));

    // We want to listen to the form's changes, skip the first one since we know that will be the initial state when Formly initializes.
    // After that any new form change events, we will debounce until the user has stopped for a second.
    // Then we will combine the initial form value into the event stream, and compare with the current.
    // If they are different we will make the api request and update the initial form value if a page refresh is not required.
    return this.form.valueChanges.pipe(
      skip(1),
      debounceTime(1000),
      mergeMap(value => zip(this.initialFormValue$, of(value))),
      filter(([initial, current]) => this.filterIfLoginOrPaymentSectionPresent()),
      filter(([initial, current]) => this.filterFormDidNotChange(initial, current)),
      switchMap(([initial, current]) => {
        this.showLoading();
        const request = this.formSaveService.createSubmitRequest(this.formData, initial, current);
        return zip(of(current), this.formSaveService.save(this.formData.uuId, request));
      }),
    )
      .subscribe(([current, response]) => {
        if (response.errors.length === 0 && response.refreshPage) { // TODO we need to think about a global error handling for these cases
          this.refreshForm();
        } else {
          this.initialFormValue$.next(current);
          this.hideLoading();
        }
      });
  }

  initFormPagerDisableSubscription(): Subscription {
    // We want to listen to the form value change events and disable the pager buttons if
    // the form does not include the login or payment sections. This prevents the user from clicking
    // a pager button when a network request is in progress or may occur which could change the pager buttons.
    return this.form.valueChanges.pipe(
      skip(1),
      filter(res => this.filterIfLoginOrPaymentSectionPresent()),
    )
      .subscribe(res => this.disablePager = true);
  }

  initFormPagerEnableSubscription(): Subscription {
    // We want to listen on the form value change events and follow the pipeline of `initFormValueChangeSubscription`
    // and handle the re-enablement of the form if the user's changes are the same as the initial state after the
    // debounce. This prevents the form pager buttons getting into a locked state if the user modifies the form,
    // but the modification is the same as the initial state.
    return this.form.valueChanges.pipe(
      skip(1),
      debounceTime(1000),
      mergeMap(value => zip(this.initialFormValue$, of(value))),
      filter(([initial, current]) => this.filterIfLoginOrPaymentSectionPresent()),
      filter(([initial, current]) => !this.filterFormDidNotChange(initial, current)),
    )
      .subscribe(res => this.disablePager = false);
  }

  filterFormDidNotChange(initial: any, current: any): boolean {
    return JSON.stringify(current) !== JSON.stringify(initial);
  }

  filterIfLoginOrPaymentSectionPresent(): boolean {
    return this.formData.sections.some(section => ![1, 6].includes(section.type));
  }

  handleFormRetrievalSuccess(data: Form): void {
    this.setupFormlyForm(data);
    this.hideLoading();
  
    const { clock = 0, clockDelay = 0 } = data.metadata ?? {};
    const prevTimerData = this.timerService.timerData$.getValue();
  
    if (prevTimerData.clock === clock && prevTimerData.clockDelay === clockDelay) return;
  
    this.timerService.showTimer$.next(clock > 0);
    this.timerService.timerData$.next({ clock, clockDelay });
  }

  setupFormlyForm(data: Form): void {

    this.formData = data;
    this.form = new FormGroup({});
    this.model = {};

    if (data.error) {
      this.error = true;
      this.errorMessage = data.errorMessage;
    } else {
      this.error = false;
      this.fieldsChangeSubscription.unsubscribe();
      this.pagerButtonsEnableSubscription.unsubscribe();
      this.pagerButtonsDisableSubscription.unsubscribe();
      this.fieldsChangeSubscription = this.initFormValueChangeSubscription();
      this.pagerButtonsEnableSubscription = this.initFormPagerEnableSubscription();
      this.pagerButtonsDisableSubscription = this.initFormPagerDisableSubscription();
      this.hasLayouts = this.formData.sections.length > 0;
      this.paymentStateService.initializePaymentState(this.formData);
      this.fields = this.createFormlySections(data.sections);
    }
  }

  createFormlySections(sections: FormSections[]): FormlyFieldConfig[] {
    return sections
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .flatMap((section) => this.createFormlySection(section));
  }

  createFormlySection(section: FormSections): FormlyFieldConfig {
    const transformElementsIntoFieldConfig: TransformElementsIntoFieldConfig = (element: FormElements): FormlyFieldConfig => {
      const compositeComponentType = this.addCompositeComponentType(element, section) as FormlyFieldConfig;
      return compositeComponentType || this.addSimpleComponentType(element);
    };

    const props: any = {
      type: section.type,
      name: section.name,
      elements: section.elements,
      displayOrder: section.displayOrder,
      border: section.border,
      totalAmountDue: section.totalAmountDue,
      takeThisForm: this.getTakeForm(),
      errorMessage: section.errorMessage,
      totalAmount: section.totalAmount,
      totalDiscountedAmount: section.totalDiscountedAmount,
      showPrices: section.showPrices,
      refreshCallback: () => this.refreshForm(),
      uuId: this.formData.uuId
    };

    const eventRegistrantButtons = section.elements.filter(e => e.type === 335);
    const createApplicantButtons = section.elements.filter(e => e.type === 347);

    const organizationElements = section.organization ? section.organization.elements : [];
    const withWrapper = !!createApplicantButtons.length;
    const applicantsFieldConfig = this.buildApplicantsFields(section, transformElementsIntoFieldConfig, withWrapper);
    const registrantFieldConfig = this.buildRegistrantsFields(section, transformElementsIntoFieldConfig);

    const filterOutElements: FormElements[] = [...eventRegistrantButtons, ...createApplicantButtons];

    const elementsFiltered = section.elements
      .filter(e => !filterOutElements.includes(e))
      .map(transformElementsIntoFieldConfig);

    return {
      wrappers: [this.getSectionType(props.type)],
      props,
      fieldGroup: [
        ...this.addSectionErrorComponent(props),
        ...elementsFiltered,
        ...organizationElements.map(transformElementsIntoFieldConfig),
        ...applicantsFieldConfig,
        ...registrantFieldConfig,
        ...createApplicantButtons.map(transformElementsIntoFieldConfig),
        ...eventRegistrantButtons.map(transformElementsIntoFieldConfig)
      ]
    };
  }

  private buildRegistrantsFields(section: FormSections, transform: TransformElementsIntoFieldConfig) {
    let registrantFieldConfig: FormlyFieldConfig[] = [];
    if (section.selectedRegistrants) {
      registrantFieldConfig = section.selectedRegistrants
        .map(registrant => ({
          wrappers: [RegistrantWrapperComponent],
          props: {
            ...registrant,
            uuId: this.formData.uuId,
            refreshCallback: () => this.refreshForm(),
            wrapperType: WrapperType.REGISTRANT,
            sectionId: section.key
          },
          fieldGroupClassName: this.registrantBodyElementClass,
          fieldGroup: registrant.elements
            .map(element => ({ ...element, registrant }))
            .map(element => transform(element))
            .map(fieldConfig => ({ ...fieldConfig, key: `registrant_${registrant.id}_${fieldConfig.key}` }))
        }));
    }
    return registrantFieldConfig;
  }

  private buildApplicantsFields(section: FormSections, transform: TransformElementsIntoFieldConfig, withWrapper: boolean) {
    if (!section.applicants) {
      return [];
    }
    if (!withWrapper) {
      return section.applicants
        .map(applicant => ({
          fieldGroup: applicant.elements
            .map(element => ({ ...element, applicant }))
            .map(element => transform(element))
            .map(fieldConfig => ({ ...fieldConfig, key: `applicant_${applicant.id}_${fieldConfig.key}` }))
        }));
    }

    return section.applicants
      .map(applicant => ({
        wrappers: [RegistrantWrapperComponent],
        props: {
          ...applicant,
          reserved: true,
          wrapperType: WrapperType.APPLICANT,
          uuId: this.formData.uuId,
          refreshCallback: () => this.refreshForm()
        },
        fieldGroupClassName: this.registrantBodyElementClass,
        fieldGroup: applicant.elements
          .map(element => ({ ...element, applicant }))
          .map(element => transform(element))
          .map(fieldConfig => ({ ...fieldConfig, key: `applicant_${applicant.id}_${fieldConfig.key}` }))
      }));
  }

  getSectionType(type: number): new (...args: any[]) => FieldWrapper {

    switch (type) {
      case 1:
        return LoginSectionComponent;
      case 3:
        return EventSectionComponent;
      case 4:
        return MembershipSectionComponent;
      case 5:
        return SummarySectionComponent;
      case 6:
        return PaymentSectionComponent;
      case 9:
        return ReviewPanelSectionComponent;
      default:
        return GeneralSectionComponent;
    }
  }

  addCompositeComponentType(element: FormElements, section: FormSections): FormlyFieldConfig | null {

    switch (element.type) {
      case 130: // just name
      case 336: // name for registrants (of an event)
      case 348: // name for applicants (of a membership)
        return this.addNameCompositeComponentType(element);
      case 150:
        return this.addAddressCompositeComponentType(element);
      case 140:
      case 160:
        return this.addEmailPhoneCompositeComponentType(element);
      case 311:
        return this.addLoginExistingCompositeComponentType(element);
      case 313:
        return this.addLoginNewCompositeComponentType(element, section.organizationName);
      case 335:
        return this.addEventButtons(section);
      case 345:
        return this.addMembershipOptions(element);
      case 346:
        return this.addAutomaticallyRenew(element);
      case 347:
        return this.addCreateNewApplicantButton(section);
      case 361: // Credit Card Composite
      case 362: // EFT Composite
      case 363: // Invoice composite
      case 364: // Offline Payment composite
        return this.addPaymentCompositeType(element, section.organizationName);
      case 405: // Review Panel File Upload
        return this.addReviewPanelFileUpload(element);
      case 1340:
        return this.addOrganizationName(element);
      default:
        return null;
    }
  }

  private addEmailPhoneCompositeComponentType(element: FormElements): FormlyFieldConfig {

    // Extract templateOptions safely
    const values = element.templateOptions?.values ?? [];
    const attributeTypeLabels = element.templateOptions?.attributeTypeLabels ?? [];

    // Generate multiple field groups dynamically based on available values
    const fieldGroups = values.map((defaultValues, index) => {

      const phoneEmailValue = defaultValues as EmailPhoneValues;
      const isRequired = attributeTypeLabels[index]?.status === 2;
      const isReadOnly = attributeTypeLabels[index]?.status === 3;
      let field: FormlyFieldConfig;

      if (element.type === 160) {

        if (element.templateOptions.organizationField) {
          field = this.createFieldWithFieldLevelValidation(TextFieldComponent, `phone`, '', phoneEmailValue.phone, {
            required: isRequired,
            readonly: isReadOnly
          })
        } else {
          field = this.createFieldWithFieldLevelValidation(TextFieldComponent, `phone`, '', phoneEmailValue.phone, {
            label: phoneEmailValue.typeLabel.name,
            required: isRequired,
            readonly: isReadOnly
          })
        }
      } else {

        if (element.templateOptions.organizationField) {
          field = this.createFieldWithFieldLevelValidation(TextFieldComponent, `email`, '', phoneEmailValue.email, {
            required: isRequired,
            readonly: isReadOnly,
            type: 'email'
          }, ["email"]);
        } else {
          field = this.createFieldWithFieldLevelValidation(TextFieldComponent, `email`, '', phoneEmailValue.email, {
            label: phoneEmailValue.typeLabel.name,
            required: isRequired,
            readonly: isReadOnly,
            type: 'email'
          }, ["email"]);
        }
        
      }

      return {
        key: this.generateFieldKey(element, index), // Unique key per fieldGroup
        className: element.templateOptions.organizationField ? 'email-phone' : 'email-phone email-phone--organization',
        props: {
          ...structuredClone(element.templateOptions), // Ensure deep clone
          label: phoneEmailValue.typeLabel.name,
        },
        fieldGroup: [
          field
        ]
      };
    });

    return {
      wrappers: [EmailPhoneComponent],
      key: element.key.toString(),
      props: {
        label: element.templateOptions.label,
        organizationField: element.templateOptions.organizationField,
        required: element.templateOptions?.organizationField ? element.templateOptions?.attributeTypeLabels[0]?.status == 2 ? true : false : false,
        readonly: element.templateOptions?.organizationField ? element.templateOptions?.attributeTypeLabels[0]?.status == 3 ? true : false : false,
      },
      fieldGroup: fieldGroups, // Assign the generated fieldGroups
    };
  }

  private generateFieldKey(element: FormElements, index: number): string {
    const { key, templateOptions } = element;
    const attribute = templateOptions.attributeTypeLabels[index];

    if (!attribute) {
      throw new Error(`Attribute at index ${index} is missing for element key ${key}`);
    }

    return `${key}_${attribute.id}_${attribute.name}`;
  }

  private generateFieldKeyAddress(element: FormElements, index: number): string {
    const { key, templateOptions } = element;
    const attribute = templateOptions.attributeTypeLabels[index];

    if (!attribute) {
      throw new Error(`Attribute at index ${index} is missing for element key ${key}`);
    }

    return `${key}_${attribute.id}_${attribute.name}_${index}`;
  }

  private addReviewPanelFileUpload(element: FormElements): FormlyFieldConfig {
    return {
      key: element.key.toString(),
      wrappers: [FieldWrapperComponent],
      type: FileFieldComponent,
      props: {
        ...element.templateOptions,
        uuId: this.formData.uuId,
        uploadType: UploadType.REVIEW_PANEL_FILE_UPLOAD,
        acceptFiles: [
          "application/msword",
          "application/pdf",
          "image/*",
          ".pdf",
          ".doc",
          ".docx",
          ".ppt",
          ".pptx"
        ]
      }
    };
  }


  private addAutomaticallyRenew(element: FormElements): FormlyFieldConfig {
    const template = element.templateOptions as any;
    const options = this.getOptions([
      {
        name: element.templateOptions.label,
        id: 0,
        value: template.selected,
        selected: template.selected,
        disabled: element.templateOptions.readonly
      } as Partial<Selection> as Selection
    ]);
    return {
      key: element.key.toString(),
      type: CheckboxComponent,
      wrappers: [FieldWrapperComponent],
      defaultValue: this.getSingleSelectDefaultValue(options),
      props: {
        options
      }
    };

  }

  private addMembershipOptions(element: FormElements): FormlyFieldConfig {
    const selections = element.templateOptions.selections;
    if (selections && selections.length === 1) {
      const [selection] = selections;
      return {
        wrappers: [FieldWrapperComponent],
        fieldGroup: [
          this.createField(LabelComponent, element.key + '-label', '', '', {
            label: element.templateOptions.label
          }),
          this.createField(LabelComponent, element.key + '-value', '', '', {
            label: selection.name
          })
        ]
      }
    }
    
    const options = this.getOptions(selections);

    return {
      key: element.key.toString(),
      type: SingleSelectComponent,
      wrappers: [FieldWrapperComponent],
      defaultValue: this.getSingleSelectDefaultValue(options),
      props: {
        options,
        clearable: false,
        ...element.templateOptions,
        memberSelection: true
      }
    };
  }

  private addOrganizationName(element: FormElements): FormlyFieldConfig {
    const templateOptions = element.templateOptions;
    return this.createFieldWithWrapper(TextFieldComponent, element.key.toString(), '', templateOptions.companyName, {
      label: element.templateOptions.label
    });
  }

  private addCreateNewApplicantButton(section: FormSections): FormlyFieldConfig {
    return {
      type: CreateLinkedProfileButtonComponent,
      props: {
        refreshCallback: () => this.refreshForm(),
        uuId: this.formData.uuId,
        sectionId: section.key,
        disabled: section.elements.filter(e => e.type === 347).at(0)?.templateOptions.disabled || false
      }
    };
  }

  private addEventButtons(section: FormSections): FormlyFieldConfig {

    // Element 335 has actually no props. Props come from the section. But we can build the components based on it
    const props: any = {
      type: section.type,
      name: section.name,
      displayOrder: section.displayOrder,
      border: section.border,
      eventName: section.eventName,
      eventDate: section.eventDate,
      required: section.required,
      canRegisterSelf: section.canRegisterSelf,
      canRegisterLinkedProfiles: section.canRegisterLinkedProfiles,
      canRegisterGuest: section.canRegisterGuest,
      availableLinkedProfiles: section.availableLinkedProfiles,
      selectedRegistrants: section.selectedRegistrants,
      showReservationInfo: section.showReservationInfo
    } as Partial<FormSections>;


    // After we add the buttons component
    return {
      props: {
        ...props,
        refreshCallback: () => this.refreshForm(),
        uuId: this.formData.uuId,
        sectionId: section.key
      },
      type: EventButtonComponent
    };
  }

  //This function is passed via input into components and as a callback into some formly fields. 
  //In a later overhaul, we should consider to refactor this into a event emitter and just consume it in the components.
  refreshForm = () => {
    this.formDataService.getFormRefresh(this.formData.uuId, this.formData.pageId)
      .subscribe(res => {
        this.handleFormRetrievalSuccess(res);
        this.hideLoading();
      });
  }

  private addNameCompositeComponentType(element: FormElements): FormlyFieldConfig {
    // TODO refactor this method into a new class
    const templateOptions = element.templateOptions as FormTemplateOptions

    const fieldGroup: FormlyFieldConfig[] = [];
    const optionsFormatter = (s: PrefixSuffix) => ({
      value: s.id,
      label: s.name,
      selected: s.selected
    });
    
    const getSelectedPrefixSuffix = (prefixes: PrefixSuffix[]): number | undefined => {
      return prefixes?.find(prefix => prefix.selected)?.id;
    }

    if (templateOptions.prefixEnabled) {
      fieldGroup.push(this.createField(SingleSelectComponent, 'prefix', 'name__prefix', getSelectedPrefixSuffix(templateOptions.prefixes), {
        options: templateOptions.prefixes.map(optionsFormatter),
        placeholder: "Prefix",
        readonly: templateOptions.readonly
      }));
    }

    fieldGroup.push(this.createField(TextFieldComponent, 'firstName', 'name__first-name', templateOptions.firstName, {
      placeholder: "First Name",
      readonly: templateOptions.readonly
    }));

    if (templateOptions.middleNameEnabled) {
      fieldGroup.push(this.createField(TextFieldComponent, 'middleName', 'name__middle-name', templateOptions.middleName, {
        placeholder: "Middle Name",
        readonly: templateOptions.readonly
      }));
    }

    if (templateOptions.middleInitialEnabled) {
      fieldGroup.push(this.createField(TextFieldComponent, 'middleInitial', 'name__middle-initial', templateOptions.middleInitial, {
        placeholder: "Middle Initial",
        readonly: templateOptions.readonly
      }));
    }

    fieldGroup.push(this.createField(TextFieldComponent, 'lastName', 'name__last-name', templateOptions.lastName, {
      placeholder: "Last Name",
      readonly: templateOptions.readonly
    }));

    if (templateOptions.suffixEnabled) {
      fieldGroup.push(this.createField(SingleSelectComponent, 'suffix', 'name__suffix', getSelectedPrefixSuffix(templateOptions.suffixes), {
        options: templateOptions.suffixes.map(optionsFormatter),
        placeholder: "Suffix",
        readonly: templateOptions.readonly
      }));
    }

    return {
      key: element.key.toString(),
      wrappers: [NameFieldComponent, FieldWrapperComponent],
      fieldGroupClassName: 'name',
      validators: { validation: ['nameRequired'] },
      props: { ...templateOptions },
      fieldGroup
    };
  }

  addLoginExistingCompositeComponentType(element: FormElements): FormlyFieldConfig {

    const fieldConfig: FormlyFieldConfig = {
      key: element.key.toString(),
      wrappers: [LoginExistingComponent],
      props: { formId: this.formData.id },
      validators: { validation: ['loginInvalid'] },
      fieldGroup: [
        this.createField(TextFieldComponent, 'username', '', null, { label: 'Username' }),
        this.createField(TextFieldComponent, 'password', '', null, { label: 'Password', type: 'password', toggle: true })
      ],
    }

    return fieldConfig;
  }

  addLoginNewCompositeComponentType(element: FormElements, organizationName?: string): FormlyFieldConfig {

    const fieldConfig: FormlyFieldConfig = {
      key: element.key.toString(),
      wrappers: [LoginNewComponent],
      validators: { validation: ['loginNewInvalid'] },
      fieldGroup: [
        this.createField(TextFieldComponent, 'email', '', null, { label: 'Email', type: 'email', helpText: `By providing your email address, you agree to receive emails from ${organizationName}` })
      ]
    }

    return fieldConfig;
  }

  addAddressCompositeComponentType(element: FormElements): FormlyFieldConfig {
    // TODO refactor this method into a new class
    const values = element.templateOptions?.values ?? [];
    const attributeTypeLabels = element.templateOptions?.attributeTypeLabels ?? [];

    const fieldGroups = values.map((value: AddressValues | EmailPhoneValues, index: number) => {

      let address = value as AddressValues;

      const isRequired = attributeTypeLabels[index].status === 2;
      const isReadOnly = attributeTypeLabels[index].status === 3;
      const stateOptions = this.getOptionsAddress(address.states.slice(1));
      const countryOptions = this.getOptionsAddress(address.countries.slice(1));

      const fieldConfig: FormlyFieldConfig = {
        key: this.generateFieldKeyAddress(element, index),
        wrappers: [AddressComponent, FieldWrapperComponent],
        fieldGroupClassName: 'address',
        validators: { validation: ['addressInvalid'] },
        props: { ...element.templateOptions, required: isRequired, index: index },
        fieldGroup: [
          this.createField(TextFieldComponent, 'line1', '', address.line1, { readonly: isReadOnly, placeholder: 'Line 1' }),
          this.createField(TextFieldComponent, 'line2', '', address.line2, { readonly: isReadOnly, placeholder: 'Line 2' }),
          {
            fieldGroupClassName: 'address__details',
            fieldGroup: [
              this.createField(TextFieldComponent, 'city', 'city', address.city, { readonly: isReadOnly, placeholder: 'City' }),
              this.createField(SingleSelectComponent, 'states', 'state', this.getSingleSelectDefaultValue(stateOptions), {
                options: stateOptions, 
                placeholder: 'Select State/Province',
                readonly: isReadOnly
              }),
              this.createField(TextFieldComponent, 'zip', 'zip', address.zip, { readonly: isReadOnly, placeholder: 'Zip/Postal Code' }),
            ],
          },
          this.createField(SingleSelectComponent, 'countries', '', this.getSingleSelectDefaultValue(countryOptions), {
            options: countryOptions, 
            placeholder: 'Select Country',
            readonly: isReadOnly
          }),
        ],
      };

      return fieldConfig;
    });

    return {
      key: element.key.toString(),
      fieldGroup: fieldGroups, // Assign the generated fieldGroups
      fieldGroupClassName: fieldGroups.length > 1 ? 'address-multiple' : ''
    };
  }

  addPaymentCompositeType(element: FormElements, organizationName: string): FormlyFieldConfig | null {
    if (361 === element.type && element.templateOptions.display) {
      return this.addCreditCardCompositeComponentType(element);
    }
    if (362 === element.type && element.templateOptions.display) {
      return this.addEFTCompositeComponentType(element, organizationName);
    }
    if (363 === element.type && element.templateOptions.display) {
      return this.addInvoicePaymentCompositeComponentType(element);
    }
    if (364 === element.type && element.templateOptions.display) {
      return this.addOfflinePaymentCompositeComponentType(element);
    }
    return null;
  }

  addCreditCardCompositeComponentType(element: FormElements): FormlyFieldConfig {
    // email and phone are dynamic fields for the credit card component
    // these fields are appended to the billing field group of this component when an EFT component is in the payment section
    const hasEFTFields = [
      this.createFieldWithFieldLevelValidation(TextFieldComponent, 'email', 'credit-card__email', element.templateOptions.email, { label: "Email", required: true }, ["email"]),
      this.createFieldWithFieldLevelValidation(TextFieldComponent, 'phone', 'credit-card__phone', element.templateOptions.phone, { label: "Phone Number", required: true }),
    ];
    // captcha is a dynamic field for the credit card component
    // this field is appended to the billing field group of this component when EFT is NOT in the payment section
    const onlyCCFields = [
      this.createFieldWithFieldLevelValidation(CaptchaComponent, 'captcha', 'credit-card__captcha', undefined, { label: "Captcha", required: true }),
    ];
    // based on if EFT is in the payment section we will return different fields to be appended to the billing field group
    const conditionalFields = this.paymentStateService.getPaymentState().hasEFT ? hasEFTFields : onlyCCFields;

    const states = this.getOptions(element.templateOptions.states);
    const countries = this.getOptions(element.templateOptions.countries);

    return {
      key: element.key.toString(),
      props: { ...element.templateOptions },
      expressions: {
        hide: this.paymentStateService.getPaymentState$().pipe(
          map(paymentState => {
            if ((paymentState.hasInvoicing && (paymentState.hasCC || paymentState.hasEFT)) || paymentState.hasOfflinePayment) {
              return !(paymentState.hasCC && paymentState.paymentType === "0");
            }
            return !paymentState.hasCC;
          })
        )
      },
      fieldGroup: [
        {
          props: { name: "Credit Card Payment" },
          wrappers: [GenericHeaderWrapperComponent],
          fieldGroupClassName: 'credit-card',
          fieldGroup: [
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'firstName', 'credit-card__first-name', element.templateOptions.firstName, { label: "First Name", required: true }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'lastName', 'credit-card__last-name', element.templateOptions.lastName, { label: "Last Name", required: true }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'company', 'credit-card__company', element.templateOptions.company, { label: "Company", placeholder: "(Optional)" }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'cardNumber', 'credit-card__number', undefined, { label: "Card Number", required: true }, ["mustBeNumeric"]),
            this.createFieldWithFieldLevelValidationAndChangeEvent(SingleSelectComponent, 'ccType', 'credit-card__type', undefined, {
              label: "Credit Card Type",
              required: true,
              options: this.getOptions(element.templateOptions.creditCards)
            }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(CvvComponent, 'securityCode', 'credit-card__cvv', undefined, { label: "CVV", required: true }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(SingleSelectComponent, 'expirationMonth', 'credit-card__expiration-month', this.calendarService.getDefaultMonth(), {
              label: "Expiration Month",
              required: true,
              options: this.calendarService.getMonths()
            }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(SingleSelectComponent, 'expirationYear', 'credit-card__expiration-year', this.calendarService.getCurrentYear(), {
              label: "Expiration Year",
              required: true,
              options: this.calendarService.getNext15Years()
            }),
          ]
        },
        {
          props: { name: "Billing Address" },
          wrappers: [GenericHeaderWrapperComponent],
          fieldGroupClassName: 'credit-card',
          fieldGroup: [
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'addressLine1', 'credit-card__address-line', element.templateOptions.addressLine1, { label: "Street Line 1", required: true }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'addressLine2', 'credit-card__address-line', element.templateOptions.addressLine2, { label: "Street Line 2", required: false }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'city', 'credit-card__city', element.templateOptions.addressCity, { label: "City", required: true }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(SingleSelectComponent, 'state', 'credit-card__state', this.getSingleSelectDefaultValue(states), {
              label: "State",
              // The state field becomes require when EFT is in the payment section
              required: this.paymentStateService.getPaymentState().hasEFT,
              options: states
            }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'zip', 'credit-card__zip', element.templateOptions.addressZip, { label: "Zip/Postal Code", required: true }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(SingleSelectComponent, 'country', 'credit-card__country', this.getSingleSelectDefaultValue(countries), {
              label: "Country",
              // The country field becomes required when EFT is in the payment section
              required: this.paymentStateService.getPaymentState().hasEFT,
              options: countries
            }),
            // Appending the conditional fields to the billing field group if the EFT is in the payment section
            ...conditionalFields
          ]
        }
      ],
    };
  }

  addEFTCompositeComponentType(element: FormElements, organizationName: string): FormlyFieldConfig {
    const states = this.getOptions(element.templateOptions.states);
    const countries = this.getOptions(element.templateOptions.countries);
    const accountTypes: any[] = [
      { value: "0", label: "Checking", selected: false },
      { value: "1", label: "Savings", selected: false }
    ];
    const accountPurposes: any[] = [
      { value: "0", label: "Business", selected: false },
      { value: "1", label: "Personal", selected: false }
    ];
    // Billing and the Electronic Fund Transfer field groups use the authorize agreement and captcha components
    // they both use them under different conditions. Billing always shows it, and the electronic fund transfer
    // displays the fields when it's in a payment section with the credit card component
    const authAgreementAndCaptchaFields = [
      {
        type: EftAuthorizeAgreementComponent,
        props: { organizationName }
      },
      this.createFieldWithFieldLevelValidation(CaptchaComponent, 'captcha', 'eft__captcha', undefined, { label: "Captcha", required: true }),
    ];
    // Defining the Billing Field group as a variable because it is a dynamic set of fields
    // Billing field group is only applied to the EFT component if credit card is NOT in the payment section
    const billingFieldGroup = {
      props: { name: "Billing Address" },
      wrappers: [GenericHeaderWrapperComponent],
      fieldGroupClassName: 'eft',
      fieldGroup: [
        this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'addressLine1', 'eft__street-line-1', element.templateOptions.addressLine1, { label: "Street Line 1", required: true }),
        this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'addressLine2', 'eft__street-line-2', element.templateOptions.addressLine2, { label: "Street Line 2" }),
        this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'city', 'eft__city', element.templateOptions.addressCity, { label: "City", required: true }),
        this.createFieldWithFieldLevelValidationAndChangeEvent(SingleSelectComponent, 'state', 'eft__state', this.getSingleSelectDefaultValue(states) || "0", {
          label: "State",
          required: true,
          options: states
        }),
        this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'zip', 'eft__zip', element.templateOptions.addressZip, { label: "Zip/Postal Code", required: true }),
        this.createFieldWithFieldLevelValidationAndChangeEvent(SingleSelectComponent, 'country', 'eft__country', this.getSingleSelectDefaultValue(countries) || "0", {
          label: "Country",
          required: true,
          options: countries
        }),
        this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'email', 'eft__email', element.templateOptions.email, { label: "Email", required: true }),
        this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'phone', 'eft__email', element.templateOptions.phone, { label: "Phone Number", required: true }),
        ...authAgreementAndCaptchaFields
      ]
    };

    // the conditional auth and captcha will return the additional fields or an empty array if the payment section has
    // a credit card component it. This applies the authorize agreement and captcha at the end of the form
    const conditionalAuthAndCaptcha = this.paymentStateService.getPaymentState().hasCC ? authAgreementAndCaptchaFields : [];
    // The Billing Field Group is only present if the EFT is in a payment section WITHOUT the credit card component.
    const conditionalBillingFieldGroup = this.paymentStateService.getPaymentState().hasCC ? [] : [billingFieldGroup];
    return {
      key: element.key.toString(),
      props: { ...element.templateOptions },
      validators: {
        validation: [
          { name: "mustMatchAccountNumbers", options: { errorPath: "confirmAccountNumber" } }
        ]
      },
      expressions: {
        hide: this.paymentStateService.getPaymentState$().pipe(
          map(paymentState => {
            if ((paymentState.hasInvoicing && (paymentState.hasCC || paymentState.hasEFT)) || paymentState.hasOfflinePayment) {
              return !(paymentState.hasEFT && paymentState.paymentType === "0");
            }
            return !paymentState.hasEFT;
          })
        )
      },
      fieldGroup: [
        {
          props: { name: "Electronic Funds Transfer" },
          wrappers: [GenericHeaderWrapperComponent],
          fieldGroupClassName: 'eft',
          fieldGroup: [
            { type: EftCheckExampleComponent },
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'routingNumber', 'eft__routing-number', undefined, { label: "Routing Number", required: true }, ["mustBeNumeric"]),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'accountNumber', 'eft__account-number', undefined, { label: "Account Number", required: true }, ["mustBeNumeric"]),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'confirmAccountNumber', 'eft__confirm-account-number', undefined, { label: "Confirm Account Number", required: true }, ["mustBeNumeric"]),
            this.createFieldWithFieldLevelValidationAndChangeEvent(SingleSelectComponent, 'accountType', 'eff__account-type', undefined, {
              label: "Account Type",
              placeholder: "Select account type",
              options: accountTypes
            }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(SingleSelectComponent, 'accountPurpose', 'eft__account-purpose', undefined, {
              label: "Account Purpose",
              placeholder: "Select account purpose",
              options: accountPurposes
            }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'name', 'eft__name', element.templateOptions.name, { label: "Full Name", required: true }),
            // dynamic fields added if the payment section has a credit card component
            ...conditionalAuthAndCaptcha
          ]
        },
        // dynamic billing field group if the payment section has a credit card component
        ...conditionalBillingFieldGroup
      ],
    };
  }
  addOfflinePaymentCompositeComponentType(element: FormElements): FormlyFieldConfig {
    const paymentTypes: any[] = [
      { value: "3", label: "Check", selected: true },
      { value: "4", label: "Cash", selected: false },
      { value: "10", label: "Offline Credit Card", selected: false },
      { value: "5", label: "Wire Transfer", selected: false },
      { value: "7", label: "Credit", selected: false },
      { value: "11", label: "No Payment", selected: false },
      { value: "6", label: "Other", selected: false }
    ];
    const defaultDate = () => {
      const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
      // @ts-ignore
      return new Intl.DateTimeFormat('en', options).format(new Date());
    }
    return {
      key: element.key.toString(),
      props: { ...element.templateOptions },
      expressions: {
        hide: this.paymentStateService.getPaymentState$().pipe(
          map(paymentState => {
            if ((paymentState.hasInvoicing && (paymentState.hasCC || paymentState.hasEFT)) || paymentState.hasOfflinePayment) {
              return !(paymentState.hasOfflinePayment && paymentState.paymentType === "3");
            }
            return !paymentState.hasOfflinePayment;
          })
        )
      },
      fieldGroup: [
        {
          props: { name: "Offline Payment" },
          wrappers: [GenericHeaderWrapperComponent],
          fieldGroupClassName: 'offline-payment',
          fieldGroup: [
            this.createFieldWithFieldLevelValidationAndChangeEvent(SingleSelectComponent, 'offlinePaymentTypeId', 'offline-payment__type', this.getSingleSelectDefaultValue(paymentTypes) || "3", {
              label: "Payment Type",
              required: true,
              options: paymentTypes,
              clearable: false
            }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'referenceNumber', 'offline-payment__reference-number', undefined, { label: "Reference/Check Number" }, ["mustBeNumeric"]),
            this.createFieldWithFieldLevelValidationAndChangeEvent(DatePickerComponent, 'paymentDate', 'offline-payment__date-received', defaultDate(), { label: "Date Received", required: true }),
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'email', 'offline-payment__email', element.templateOptions.email, { label: "Email", required: true }, ["email"]),
          ]
        },
      ],
    };
  }

  addInvoicePaymentCompositeComponentType(element: FormElements): FormlyFieldConfig {
    return {
      key: element.key.toString(),
      props: { ...element.templateOptions },
      expressions: {
        hide: this.paymentStateService.getPaymentState$().pipe(
          map(paymentState => {
            if ((paymentState.hasInvoicing && (paymentState.hasCC || paymentState.hasEFT)) || paymentState.hasOfflinePayment) {
              return !(paymentState.hasInvoicing && paymentState.paymentType === "1");
            }
            return !paymentState.hasInvoicing;
          })
        )
      },
      fieldGroup: [
        {
          props: { name: "Invoice" },
          wrappers: [GenericHeaderWrapperComponent],
          fieldGroupClassName: 'invoice',
          fieldGroup: [
            this.createFieldWithFieldLevelValidationAndChangeEvent(TextFieldComponent, 'invoiceEmail', 'invoice__email', element.templateOptions.email, { label: "Invoice Email", required: true }, ["email"]),
          ]
        },
      ],
    };
  }

  createField(type: any, key: string, className: string, defaultValue?: any, props?: FormlyFieldProps & AdditionalProperties): FormlyFieldConfig {
    return { type, key, className, defaultValue, props };
  }

  createFieldWithWrapper(type: any, key: string, className: string, defaultValue?: any, props?: FormlyFieldProps & AdditionalProperties): FormlyFieldConfig {
    return { type, key, className, defaultValue, props, wrappers: [FieldWrapperComponent] };
  }

  createFieldWithFieldLevelValidation(type: any, key: string, className: string, defaultValue?: any, props?: FormlyFieldProps & AdditionalProperties, validators: string[] = []): FormlyFieldConfig {
    return { ...this.createField(type, key, className, defaultValue, props), validators: { validation: validators }, wrappers: [FieldWrapperComponent] };
  }

  createFieldWithFieldLevelValidationAndChangeEvent(type: any, key: string, className: string, defaultValue?: any, props?: FormlyFieldProps & AdditionalProperties, validators: string[] = []): FormlyFieldConfig {
    return {
      ...this.createField(type, key, className, defaultValue, props),
      validators: { validation: validators },
      wrappers: [FieldWrapperComponent],
      hooks: {
        onChanges: (field: FormlyFieldConfig) => this.handleOnChanges(field),
        onDestroy: (field: FormlyFieldConfig) => this.handleOnDestroy(field)
      }
    };
  }

  addSimpleComponentType(element: FormElements): FormlyFieldConfig {

    const fieldConfig: FormlyFieldConfig = {
      key: element.key.toString(),
      wrappers: [FieldWrapperComponent],
      type: this.getComponentType(element),
      props: element.templateOptions
    };

    fieldConfig.props!!['uuId'] = this.formData.uuId;

    // Add options for selectable types and set default values
    if ([CheckboxComponent, MultiSelectComponent, RadioComponent, SingleSelectComponent].includes(fieldConfig.type as any)) {

      const options = this.getOptions(element.templateOptions.selections) as any[]
      fieldConfig.props!!.options = options;

      if (CheckboxComponent === fieldConfig.type) {
        fieldConfig.defaultValue = this.getCheckboxDefaultValues(options);
      }

      if (MultiSelectComponent === fieldConfig.type) {
        fieldConfig.defaultValue = this.getMultiSelectDefaultValues(options);
      }

      if (RadioComponent === fieldConfig.type) {
        fieldConfig.defaultValue = this.getRadioDefaultValue(options, fieldConfig.props!!.required as boolean);
      }

      if (SingleSelectComponent === fieldConfig.type) {
        fieldConfig.defaultValue = this.getSingleSelectDefaultValue(options);
      }

    } else {
      fieldConfig.defaultValue = element.templateOptions.value || null;
    }

    if ([TextFieldComponent].includes(fieldConfig.type as any) && element.type === 170) {
      fieldConfig.defaultValue = element.templateOptions.companyName;
    }

    if ([TextFieldComponent].includes(fieldConfig.type as any) && element.type === 120) {
      fieldConfig.defaultValue = element.templateOptions.displayName;
    }

    if ([FileFieldComponent].includes(fieldConfig.type as any) && (element.type === 206 || element.type === 7)) {
      fieldConfig.props = {
        ...fieldConfig.props,
        uploadType: UploadType.DEFAULT_FILE_UPLOAD,
        acceptFiles: [
          "image/*"
        ]
      }
    }

    // Add min and max length validators if it's on the element template option
    if (element.templateOptions.minlength !== undefined) {
      fieldConfig.validators = this.addMinlengthValidator(fieldConfig.validators, element.templateOptions.minlength);
    }
    if (element.templateOptions.maxlength !== undefined) {
      fieldConfig.validators = this.addMaxLengthValidator(fieldConfig.validators, element.templateOptions.maxlength);
    }

    return fieldConfig
  }

  addSectionErrorComponent(props: any): FormlyFieldConfig[] {
    if (props.errorMessage) {
      return [
        {
          type: SectionErrorMessageComponent,
          props: { message: props.errorMessage }
        },
      ];
    }
    return [];
  }

  getOptions(options: Selection[]) {
    
    if (!options || options.length === 0) {
      return [];
    }

    return options
      .filter(option => option.name.toLowerCase() !== '(none)' && option.name.toLowerCase() !== 'make a selection') // Exclude options with name "(none)" and "make a selection"
      .map(option => ({
        value: option.id.toString(),
        label: option.name,
        selected: option.selected,
        disabled: option.disabled,
        readonly: option.readonly
      }));
  }


  getOptionsAddress(options: AddressInformation[]) {
    return options
      .filter(option => option.name.toLowerCase() !== 'select country' && option.name.toLowerCase() !== 'select state/province') // Exclude options with name "select country" and "select state/province"
      .map(option => ({
        value: option.id.toString(),
        label: option.name,
        selected: option.selected
      }));
  }

  getCheckboxDefaultValues(options: any[]): any[] {
    return options.filter(option => option.selected)
      .reduce((acc, option) => ({ ...acc, [option.value]: option.selected }), {});
  }

  getMultiSelectDefaultValues(options: any[]): string[] {
    return options.filter(option => option.selected)
      .map(option => option.value);
  }

  getRadioDefaultValue(options: any[], required: boolean): string {
    let selectedValue = options.find(option => option.selected)?.value;

    if (!selectedValue && !required) {
      selectedValue = options.find(option => option.value === '0')?.value;
    }

    return selectedValue;
  }

  getSingleSelectDefaultValue(options: any[]): string {
    return options.find(option => option.selected)?.value;
  }

  getComponentType(element: FormElements): any {

    const { type, templateOptions } = element;
    const displayType = templateOptions?.displayType;
    const multiple = templateOptions?.multiple;

    switch (type) {
      case 1:
      case 403: // Review Panel Submission Text
      case 401: // Review panel Submission Title
        return displayType === 0 ? TextFieldComponent : TextAreaComponent;
      case 2:
      case 201:
        return NumberInputComponent;
      case 3:
      case 202:
        return DatePickerComponent;
      case 4:
      case 203:
      case 404: // Review panel Submission Category
        switch (displayType) {
          case 0:
            return RadioComponent;
          case 1:
            return SingleSelectComponent;
          case 2:
            return CheckboxComponent;
          case 3:
            return MultiSelectComponent;
          default:
            return undefined;
        }
      case 5:
      case 204:
        if(multiple) return MultiSelectComponent;
        else return SingleSelectComponent;
      case 7:
      case 8:
      case 206:
      case 207:
        return FileFieldComponent;
      case 120:
      case 170:
      case 200:
      case 208:
        return TextFieldComponent;
      case 6:
      case 205:
        if(multiple) return MultiSelectComponent;
        else return SingleSelectComponent;
      case 344: // Member Type
        return SingleSelectComponent;
      //  Label fields
      case 50:  // Label Presentation
      case 333: // Event Section Label
      case 341: // Label "Choose a member type below to join as a new member"
      case 342: // Renewal Section Label
      case 375: // Confirmation Section Label
      case 402: // Review panel - main description
        return LabelComponent
      case 51:
        return LineSeparatorComponent;
      case 52:
        return WhiteSpaceComponent;
      default:
        return undefined;
    }
  }

  showLoading(): void {
    this.disablePager = true;
    this.loadingService.setLoading(true);
  }

  hideLoading(): void {
    this.disablePager = false;
    this.loadingService.setLoading(false);
  }

  showPager(): boolean {
    return this.formData.pageId !== 1 && this.hasLayouts;
  }

  showPrev(): boolean {
    // should not be first page nor confirmation page (type 7)
    return !this.formData.metadata.firstPage && this.formData.sections.filter(section => section.type === 7).length === 0;
  }

  onPrevious(): void {

    this.scrollToForm();

    this.showLoading();
    this.formDataService.getPage(this.formData.uuId, this.formData.pageId, "prev")
      .subscribe((data: Form) => this.handleFormRetrievalSuccess(data));
  }

  showNext(): boolean {
    return !this.formData.metadata.submitPage && !this.formData.metadata.lastPage;
  }

  showTimer(): boolean {
    return this.formData.metadata.clock != 0 && this.formData.metadata.clockDelay != 0;
  }

  onNext(): void {

    this.scrollToForm();

    // if required sections or form invalid, update ui and prevent next action
    if (!this.hasRequiredSections() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.showLoading();
    this.formDataService.getPage(this.formData.uuId, this.formData.pageId, "next")
      .subscribe((data: Form) => this.handleFormRetrievalSuccess(data));
  }

  showPaymentButtons$(): Observable<boolean> {
    return this.paymentStateService.paymentState$.pipe(
      map(paymentState => paymentState.paymentPage)
    );
  }

  isSubmitPage(): boolean {
    return this.formData.metadata.submitPage;
  }

  showSubmit$(): Observable<boolean> {
    return this.paymentStateService.paymentState$.pipe(
      map(paymentState => !paymentState.paymentPage && !paymentState.hideSubmit)
    );
  }

  onSubmit(): void {

    this.scrollToForm();

    if (!this.hasRequiredSections() || this.form.invalid) {
      this.form.markAllAsTouched();
      this.showValidationToast('The event section requires that you register at least one person');
      return;
    }

    this.showLoading();
    this.formSubmitService.submit({ sendInvoice: false, uuId: this.formData.uuId })
      .pipe(switchMap(response => {
        this.submissionId = response.submissionId;
        return this.formDataService.getPage(this.formData.uuId, this.formData.pageId, "next")
      }))
      .subscribe({
        next: (data) => this.handleFormRetrievalSuccess(data),
        error: (err) => this.handleFormSubmitError(err)
      });
  }

  private handleFormSubmitError(error: any): void {
    if (error instanceof HttpErrorResponse) {
      const httpError: HttpErrorResponse = error as HttpErrorResponse;
      if (httpError.status >= 400) {
        if (httpError.status === 400 && httpError.error && httpError.error.errors && httpError.error.errors[0] && httpError.error.errors[0].message) {
          if (httpError.error.errors[0].message.indexOf("The pricing for this form has changed.") > -1) {
            this.genericModalService.setRestartModal("One or more form fields has changed since you began this form. Please review your selections and try again.");
          } else {
            this.genericModalService.setRestartModal(httpError.error.errors[0].message);
          }
        } else {
          this.genericModalService.setRestartModal('An error has occurred, this form will be reset. If this issue repeats, contact your administrator.');
        }
      }
    }

    this.loadingService.setLoading(false);
  }

  /**
  * Displays a toast message if one is not already present.
  */
  private showValidationToast(message: string): void {
    if (this.isToastVisible()) {
      return;
    }

    const alert = this.createToast(message, 'danger', 'shield-exclamation');
    document.body.append(alert);
    alert.toast();
  }

  /**
  * Checks if a toast is already visible.
  */
  private isToastVisible(): boolean {
    return !!document.querySelector('sl-alert.custom-toast');
  }

  /**
  * Creates a Shoelace toast notification.
  */
  private createToast(message: string, variant: string, icon: string): SlAlert {
    return Object.assign(document.createElement('sl-alert'), {
      variant,
      closable: true,
      duration: Infinity, // Toast does not disappear automatically
      className: 'custom-toast', // Unique class to track the toast
      innerHTML: `
        <sl-icon name="${icon}" slot="icon"></sl-icon>
        ${message}
      `
    });
  }

  showPayLater$(): Observable<boolean> {
    return this.paymentStateService.paymentState$.pipe(
      map(paymentState => paymentState.hasInvoicing && (!paymentState.hasMultiplePayments || "1" === paymentState.paymentType))
    );
  }

  onPayLater(): void {

    if (!this.hasRequiredSections() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // find invoice element in payment section
    const element = this.formData.sections.find(section => 6 === section.type)?.elements.find(element => 363 === element.type);
    // @ts-ignore
    const invoiceForm = this.model[element.key];
    const request: SubmitInvoicePaymentRequest = {
      sendInvoice: true,
      uuId: this.formData.uuId,
      email: invoiceForm["invoiceEmail"] || "",
    }

    this.showLoading();
    this.formSubmitService.submitInvoicePayment(request)
      .subscribe({
        next: (res) => {
          this.submissionId = res.submissionId;
          this.formDataService.getPage(this.formData.uuId, this.formData.pageId, "next")
            .subscribe((data: Form) => this.handleFormRetrievalSuccess(data));
        },
        error: (error) => {
          this.loadingService.setLoading(false);
          this.handleSubmitInvoiceError(error);
        }
      });
  }

  private handleSubmitInvoiceError(error: any): void {
    if (error instanceof HttpErrorResponse) {
      const httpError: HttpErrorResponse = error as HttpErrorResponse;
      if (httpError.status >= 400) {
        if (httpError.status === 400 && httpError.error && httpError.error.errors && httpError.error.errors[0] && httpError.error.errors[0].message) {
          if (httpError.error.errors[0].message.indexOf("The pricing for this form has changed.") > -1) {
            this.genericModalService.setRestartModal("One or more form fields has changed since you began this form. Please review your selections and try again.");
          } else {
            this.genericModalService.setRestartModal(httpError.error.errors[0].message);
          }
        } else {
          this.genericModalService.setRestartModal('An error has occurred, this form will be reset. If this issue repeats, contact your administrator.');
        }
      }
    }

    this.loadingService.setLoading(false);
  }

  showSubmitPayment$(): Observable<boolean> {
    return this.paymentStateService.paymentState$.pipe(
      map(paymentState => "0" === paymentState.paymentType || (!paymentState.hasMultiplePayments && !paymentState.hidePaymentBtns))
    );
  }

  onSubmitPayment(): void {

    if (!this.hasRequiredSections() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.paymentStateService.getPaymentState().hasCC && this.paymentStateService.getPaymentState().hasEFT) {
      this.handleSubmitPaymentCreditCardAndEFT();
    } else if (this.paymentStateService.getPaymentState().hasCC) {
      this.handleSubmitPaymentCreditCard();
    } else if (this.paymentStateService.getPaymentState().hasEFT) {
      this.handleSubmitPaymentEFT();
    } else {
      console.error("Unknown payment type for submission");
    }
  }

  /**
   * Returns the form values related to the Credit Card component
   *
   * @private
   */
  private getCreditCardForm(): any {
    const element = this.formData.sections.find(section => 6 === section.type)
      ?.elements.find(element => 361 === element.type);
    // @ts-ignore
    return this.model[element.key];
  }

  /**
   * Returns the form values related to the EFT component
   *
   * @private
   */
  private getEFTForm(): any {
    const element = this.formData.sections.find(section => 6 === section.type)
      ?.elements.find(element => 362 === element.type);
    // @ts-ignore
    return this.model[element.key];
  }

  /**
   * Function to handle the subscribe logic for `handleSubmitPaymentCreditCardAndEFT` and
   * `handleSubmitPaymentCreditCard` payment submit network calls.
   *
   * @private
   */
  private handlePaymentSubmission(captchaId: number): any {
    return {
      next: (res: SubmitPaymentResponse) => {
        this.submissionId = res.submissionId;
        this.formDataService.getPage(this.formData.uuId, this.formData.pageId, "next")
          .subscribe((data: Form) => this.handleFormRetrievalSuccess(data));
      },
      error: (error: any) => {
        this.loadingService.setLoading(false);
        this.handleSubmitPaymentError(error, captchaId);
      }
    };
  }

  private handleSubmitPaymentCreditCardAndEFT(): void {

    const creditCardForm = this.getCreditCardForm();
    const eftForm = this.getEFTForm();
    const captchaWidgetId: number = eftForm["captcha"]["reCaptchaWidgetId"];

    const request: SubmitCreditCardAndEFTPaymentRequest = {
      sendInvoice: false,
      uuId: this.formData.uuId,
      firstName: creditCardForm["firstName"] || "",
      lastName: creditCardForm["lastName"] || "",
      company: creditCardForm["company"] || "",
      addressLine1: creditCardForm["addressLine1"] || "",
      addressLine2: creditCardForm["addressLine2"] || "",
      city: creditCardForm["city"] || "",
      state: creditCardForm["state"] || "",
      zip: creditCardForm["zip"] || "",
      country: creditCardForm["country"] || "",
      cardNumber: creditCardForm["cardNumber"] || "",
      securityCode: creditCardForm["securityCode"] || "",
      expirationMonth: creditCardForm["expirationMonth"] || 0,
      expirationYear: creditCardForm["expirationYear"] || 0,
      email: creditCardForm["email"] || "",
      phone: creditCardForm["phone"] || "",
      name: eftForm["name"] || "",
      accountNumber: eftForm["accountNumber"] || "",
      routingNumber: eftForm["routingNumber"] || "",
      accountType: eftForm["accountType"] || "",
      accountPurpose: eftForm["accountPurpose"] || "",
      reCaptchaResponseToken: eftForm["captcha"]["reCaptchaResponseToken"] || "",
    }

    this.loadingService.setLoading(true);
    this.formSubmitService.submitCreditCardAndEFTPayment(request)
      .subscribe(this.handlePaymentSubmission(captchaWidgetId));
  }

  private handleSubmitPaymentCreditCard(): void {

    const creditCardForm = this.getCreditCardForm();
    const captchaWidgetId: number = creditCardForm["captcha"]["reCaptchaWidgetId"];

    const request: SubmitCreditCardPaymentRequest = {
      sendInvoice: false,
      uuId: this.formData.uuId,
      firstName: creditCardForm["firstName"] || "",
      lastName: creditCardForm["lastName"] || "",
      company: creditCardForm["company"] || "",
      addressLine1: creditCardForm["addressLine1"] || "",
      addressLine2: creditCardForm["addressLine2"] || "",
      city: creditCardForm["city"] || "",
      state: creditCardForm["state"] || "",
      zip: creditCardForm["zip"] || "",
      country: creditCardForm["country"] || "",
      cardNumber: creditCardForm["cardNumber"] || "",
      securityCode: creditCardForm["securityCode"] || "",
      expirationMonth: creditCardForm["expirationMonth"] || 0,
      expirationYear: creditCardForm["expirationYear"] || 0,
      reCaptchaResponseToken: creditCardForm["captcha"]["reCaptchaResponseToken"] || ""
    }

    this.loadingService.setLoading(true);
    this.formSubmitService.submitCreditCardPayment(request)
      .subscribe(this.handlePaymentSubmission(captchaWidgetId));
  }

  private handleSubmitPaymentEFT(): void {

    const eftForm = this.getEFTForm();
    const captchaWidgetId = eftForm["captcha"]["reCaptchaWidgetId"];

    const request: SubmitEFTPaymentRequest = {
      sendInvoice: false,
      uuId: this.formData.uuId,
      name: eftForm["name"] || "",
      accountNumber: eftForm["accountNumber"] || "",
      routingNumber: eftForm["routingNumber"] || "",
      accountType: eftForm["accountType"] || "",
      accountPurpose: eftForm["accountPurpose"] || "",
      addressLine1: eftForm["addressLine1"] || "",
      addressLine2: eftForm["addressLine2"] || "",
      city: eftForm["city"] || "",
      state: eftForm["state"] || "",
      zip: eftForm["zip"] || "",
      country: eftForm["country"] || "",
      email: eftForm["email"] || "",
      phone: eftForm["phone"] || "",
      reCaptchaResponseToken: eftForm["captcha"]["reCaptchaResponseToken"] || ""
    }

    this.loadingService.setLoading(true);
    this.formSubmitService.submitEFTPayment(request)
      .subscribe(this.handlePaymentSubmission(captchaWidgetId));
  }

  private handleSubmitPaymentError(error: any, captchaId: number): void {
    if (error instanceof HttpErrorResponse) {
      const httpError: HttpErrorResponse = error as HttpErrorResponse;
      if (httpError.status >= 400) {
        if (httpError.status === 400 && httpError.error && httpError.error.errors && httpError.error.errors[0] && httpError.error.errors[0].message) {
          if (httpError.error.errors[0].message.indexOf("The pricing for this form has changed.") > -1) {
            this.genericModalService.setRestartModal("One or more form fields has changed since you began this form. Please review your selections and try again.");
          } else if (httpError.error.errors[0].message.indexOf("This discount code") > -1) {
            this.genericModalService.setRestartModal(httpError.error.errors[0].message);
          } else if (httpError.error.errors[0].message.indexOf("Profile information has") > -1) {
            this.genericModalService.setRestartModal(httpError.error.errors[0].message);
          } else {
            this.genericModalService.setAlertModal(error.error.errors[0].message);
          }
        }
      } else if (error.status == 500) {
        this.genericModalService.setRestartModal("An error has occurred, this form will be reset. If this issue repeats, contact your administrator.");
      } else {
        if (error.error && error.error.errors && error.error.errors.length) {
          this.genericModalService.setAlertModal(error.error.errors[0].message);
        }
      }
      this.captchaService.resetCaptcha(captchaId);
    }
    this.loadingService.setLoading(false);
  }

  showSubmitOfflinePayment$(): Observable<boolean> {
    return this.paymentStateService.paymentState$.pipe(
      map(paymentState => "3" === paymentState.paymentType)
    );
  }

  onSubmitOfflinePayment(): void {

    if (!this.hasRequiredSections() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formatDate = () => {
      // The server needs ISO string format, and the form return human-readable string version of the date.
      // We want to convert this to date type object, then applied the organization time zone
      // and then format to ISO.
      const formDateString = offlinePaymentForm["paymentDate"];
      const formDate = new Date(formDateString);
      const appliedTimeZoneDate = new Date(formDate.toLocaleString('en-US', { timeZone: this.timezoneSetting }));
      return appliedTimeZoneDate.toISOString();
    }

    // find offline payment element in payment section
    const element = this.formData.sections.find(section => 6 === section.type)?.elements.find(element => 364 === element.type);
    // @ts-ignore
    const offlinePaymentForm = this.model[element.key];
    const request: SubmitOfflinePaymentRequest = {
      sendInvoice: false,
      uuId: this.formData.uuId,
      offlinePaymentTypeId: parseInt(offlinePaymentForm["offlinePaymentTypeId"]),
      referenceNumber: offlinePaymentForm["referenceNumber"] || "",
      paymentDate: formatDate() || "",
      email: offlinePaymentForm["email"] || "",
    }

    this.loadingService.setLoading(true);
    this.formSubmitService.submitOfflinePayment(request)
      .subscribe({
        next: (res) => {
          this.submissionId = res.submissionId;
          this.formDataService.getPage(this.formData.uuId, this.formData.pageId, "next")
            .subscribe((data: Form) => this.handleFormRetrievalSuccess(data));
        },
        error: (error) => {
          this.loadingService.setLoading(false);
          this.handleOfflinePaymentError(error);
        }
      });
  }

  private handleOfflinePaymentError(error: any): void {
    if (error instanceof HttpErrorResponse) {
      const httpError: HttpErrorResponse = error as HttpErrorResponse;
      if (httpError.status >= 400) {
        if (httpError.status === 400 && httpError.error && httpError.error.errors && httpError.error.errors[0] && httpError.error.errors[0].message) {
          if (httpError.error.errors[0].message.indexOf("The pricing for this form has changed.") > -1) {
            this.genericModalService.setRestartModal("One or more form fields has changed since you began this form. Please review your selections and try again.");
          } else {
            this.genericModalService.setRestartModal(httpError.error.errors[0].message);
          }
        } else {
          this.genericModalService.setRestartModal('An error has occurred, this form will be reset. If this issue repeats, contact your administrator.');
        }
      }
    }

    this.loadingService.setLoading(false);
  }

  showFinish(): boolean {
    return !this.formData.metadata.submitPage && this.formData.metadata.lastPage;
  }

  onFinish(): void {

    this.scrollToForm();

    this.showLoading();
    this.formFinishService.finish({ submissionId: this.submissionId, forwardUrl: this.formData.forwardUrl })
      .subscribe({
        //@ts-ignore
        next: (response) => window.location = response,
        error: (err) => {
          this.hideLoading();
          console.error("error occurred determining form finish logic", err);
        }
      });
  }

  private scrollToForm() {
    const formElement = document.querySelector('app-form'); // Adjust the selector if needed
    if (formElement) {
      const offsetTop = formElement.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: offsetTop - 200, behavior: 'instant' });
    }
  }

  private addMinlengthValidator(validators: any, minLength: number): any {
    return {
      ...validators,
      minlength: {
        expression: (c: AbstractControl) => c.value ? c.value.length > 0 && c.value.length >= minLength : true
      },
    };
  }

  private addMaxLengthValidator(validators: any, maxLength: number): any {
    return {
      ...validators,
      maxlength: {
        expression: (c: AbstractControl) => c.value ? c.value.length > 0 && c.value.length <= maxLength : true
      },
    };
  }

  private hasRequiredSections(): boolean {
    return this.hasRequiredRegistrants() && this.hasRequiredMembers();
  }

  private hasRequiredRegistrants(): boolean {

    let hasRegistrants = true;

    this.formData.sections.forEach(section => {
      if (section.type === 3 && section.required && section.selectedRegistrants.length === 0) {
        hasRegistrants = false;
        this.showValidationToast('The event section requires that you register at least one person');
      }
    });

    return hasRegistrants;
  }

  private hasRequiredMembers(): boolean {

    let hasMembers = true;

    this.formData.sections.forEach(section => {
      if (section.type === 4) {

        const membershipSection = section.elements.find(elem =>
          elem.templateOptions?.memberSelection ?? false
        );

        if (membershipSection && membershipSection.templateOptions.required && membershipSection.value() === 0) {
          hasMembers = false;
          this.showValidationToast('The membership section requires that you choose at least one membership type');
        }
      }
    });

    return hasMembers;
  }

  private handleOnChanges(field: FormlyFieldConfig) {
    if (!field.formControl) {
      return;
    }

    const subscription: Subscription = field.formControl.valueChanges.pipe(
      debounceTime(1000), // Wait for user to stop typing
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      switchMap(() => this.formEventRegistrationService.refreshReservations(this.formData.uuId))
    ).subscribe();

    this.refreshReservationsSubscriptions.set(field.id, subscription);
  }

  private handleOnDestroy(field: FormlyFieldConfig) {
    this.refreshReservationsSubscriptions.forEach((subscription, key) => {
      if (key === field.id) {
        subscription.unsubscribe();
        this.refreshReservationsSubscriptions.delete(key);
      }
    });
  }
}
